/**
 * Relative date filters (Today / Tomorrow / Yesterday)
 *
 * AG Grid drops custom `params` on date filters, so `delta` cannot live in the
 * grid model alone. We keep deltas in memory and only attach them when saving.
 *
 * Call-site API:
 *   setFilterModel(relativeDatesForGrid(model))  // load / apply preset
 *   relativeDatesForSave(model)                  // before cache/saved-search save
 *   clearRelativeDates()                         // clear all filters
 *
 * RelativeDateInput Today/Tomorrow/Yesterday → noteRelativeDateButton(...)
 */
import { FilterModel } from '@ag-grid-community/core';

export type RelativeDateParams = {
  delta?: number;
  fromDelta?: number;
  toDelta?: number;
};

const deltasByColumn = new Map<string, RelativeDateParams>();

/** Last Today/Tomorrow/Yesterday click — survives brief AG Grid model churn. */
let lastButtonClick: {
  offset: number;
  dateKey: string;
  colId?: string;
} | null = null;

const RELATIVE_TYPE_OFFSETS: Record<string, number> = {
  today: 0,
  tomorrow: 1,
  yesterday: -1,
};

const localToday = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const toAgGridDateFrom = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day} 00:00:00`;
};

const dateFromOffset = (offset: number): string => {
  const date = localToday();
  date.setDate(date.getDate() + offset);
  return toAgGridDateFrom(date);
};

const dateKey = (dateFrom: unknown): string | null => {
  if (typeof dateFrom !== 'string' || dateFrom.length < 10) return null;
  return dateFrom.substring(0, 10);
};

const expectedKeyForOffset = (offset: number): string =>
  dateFromOffset(offset).substring(0, 10);

const onlyDeltas = (
  params: RelativeDateParams | undefined,
): RelativeDateParams | null => {
  if (!params) return null;

  const next: RelativeDateParams = {};
  if (params.delta !== undefined) next.delta = params.delta;
  if (params.fromDelta !== undefined) next.fromDelta = params.fromDelta;
  if (params.toDelta !== undefined) next.toDelta = params.toDelta;

  return Object.keys(next).length > 0 ? next : null;
};

const isEqualsDateOn = (
  filter: Record<string, unknown>,
  key: string,
): boolean =>
  filter.filterType === 'date' &&
  filter.type === 'equals' &&
  dateKey(filter.dateFrom) === key;

/** True when live dateFrom/dateTo still match the remembered rolling offsets. */
const liveFilterMatchesRelative = (
  filter: Record<string, unknown>,
  params: RelativeDateParams,
): boolean => {
  if (filter.filterType !== 'date') return false;

  if (params.delta !== undefined) {
    return (
      filter.type === 'equals' &&
      dateKey(filter.dateFrom) === expectedKeyForOffset(params.delta)
    );
  }

  if (params.fromDelta !== undefined || params.toDelta !== undefined) {
    if (filter.type !== 'inRange') return false;
    if (
      params.fromDelta !== undefined &&
      dateKey(filter.dateFrom) !== expectedKeyForOffset(params.fromDelta)
    ) {
      return false;
    }
    if (
      params.toDelta !== undefined &&
      dateKey(filter.dateTo) !== expectedKeyForOffset(params.toDelta)
    ) {
      return false;
    }
    return true;
  }

  return false;
};

/** Record a Today/Tomorrow/Yesterday button press. */
export const noteRelativeDateButton = (
  offset: number,
  selectedDateKey: string,
  colId?: string,
): void => {
  lastButtonClick = { offset, dateKey: selectedDateKey, colId };
};

/** Manual date edit — stop treating the filter as relative. */
export const clearRelativeDateButton = (): void => {
  lastButtonClick = null;
};

export const setColumnRelativeDate = (
  column: string,
  params: RelativeDateParams | null,
): void => {
  const deltas = onlyDeltas(params ?? undefined);
  if (deltas == null) {
    deltasByColumn.delete(column);
    return;
  }
  deltasByColumn.set(column, deltas);
};

export const getColumnRelativeDate = (
  column: string,
): RelativeDateParams | undefined => deltasByColumn.get(column);

export const clearColumnRelativeDate = (column: string): void => {
  deltasByColumn.delete(column);
};

export const relativeDateColumns = (): string[] => [...deltasByColumn.keys()];

export const clearRelativeDates = (): void => {
  deltasByColumn.clear();
  lastButtonClick = null;
};

/**
 * Remember deltas from a loaded/preset model.
 * Columns still present without relative params are cleared — load/preset
 * models keep params when relative, unlike live AG Grid models.
 */
const rememberRelativeDates = (
  filterModel: FilterModel | null | undefined,
): void => {
  if (filterModel == null) {
    deltasByColumn.clear();
    return;
  }

  for (const column of [...deltasByColumn.keys()]) {
    if (!(column in filterModel)) {
      deltasByColumn.delete(column);
      continue;
    }

    const filter = filterModel[column];
    if (!filter || typeof filter !== 'object') {
      deltasByColumn.delete(column);
      continue;
    }

    const dateFilter = filter as Record<string, unknown>;
    if (dateFilter.filterType !== 'date') {
      deltasByColumn.delete(column);
      continue;
    }

    const deltas = onlyDeltas(
      dateFilter.params as RelativeDateParams | undefined,
    );
    if (deltas == null) {
      deltasByColumn.delete(column);
    }
  }

  for (const [column, filter] of Object.entries(filterModel)) {
    if (!filter || typeof filter !== 'object') continue;

    const dateFilter = filter as Record<string, unknown>;
    if (dateFilter.filterType !== 'date') continue;

    const deltas = onlyDeltas(
      dateFilter.params as RelativeDateParams | undefined,
    );
    if (deltas != null) {
      deltasByColumn.set(column, deltas);
    }
  }
};

/** Push last button click into the column map (scoped to colId when known). */
const syncButtonClickIntoMap = (filterModel: FilterModel): void => {
  if (!lastButtonClick) return;

  const { offset, dateKey: key, colId } = lastButtonClick;

  const trySet = (column: string, filter: Record<string, unknown>): boolean => {
    if (isEqualsDateOn(filter, key)) {
      deltasByColumn.set(column, { delta: offset });
      return true;
    }

    const conditions = filter.conditions;
    if (!Array.isArray(conditions)) return false;

    for (const condition of conditions) {
      if (
        condition &&
        typeof condition === 'object' &&
        isEqualsDateOn(condition as Record<string, unknown>, key)
      ) {
        deltasByColumn.set(column, { delta: offset });
        return true;
      }
    }
    return false;
  };

  if (colId) {
    const filter = filterModel[colId];
    if (filter && typeof filter === 'object') {
      trySet(colId, filter as Record<string, unknown>);
    }
    return;
  }

  for (const [column, filter] of Object.entries(filterModel)) {
    if (!filter || typeof filter !== 'object') continue;
    trySet(column, filter as Record<string, unknown>);
  }
};

const expandRollingDates = (
  filter: Record<string, unknown>,
): Record<string, unknown> => {
  const params = filter.params as RelativeDateParams | undefined;
  if (!params) return filter;

  if (filter.type === 'equals' && params.delta !== undefined) {
    return {
      ...filter,
      dateFrom: dateFromOffset(params.delta),
      dateTo: null,
    };
  }

  if (
    filter.type === 'inRange' &&
    (params.fromDelta !== undefined || params.toDelta !== undefined)
  ) {
    return {
      ...filter,
      dateFrom:
        params.fromDelta !== undefined
          ? dateFromOffset(params.fromDelta)
          : filter.dateFrom,
      dateTo:
        params.toDelta !== undefined
          ? dateFromOffset(params.toDelta)
          : filter.dateTo,
    };
  }

  return filter;
};

const normalizeLegacyRelativeTypes = (
  filterModel: FilterModel,
): FilterModel => {
  const normalized = { ...filterModel };

  for (const [column, filter] of Object.entries(normalized)) {
    if (!filter || typeof filter !== 'object') continue;

    const dateFilter = filter as Record<string, unknown>;
    if (dateFilter.filterType !== 'date') continue;

    const type = dateFilter.type;
    if (typeof type !== 'string' || !(type in RELATIVE_TYPE_OFFSETS)) continue;

    const offset = RELATIVE_TYPE_OFFSETS[type];
    normalized[column] = {
      ...dateFilter,
      type: 'equals',
      dateFrom: dateFromOffset(offset),
      dateTo: null,
      params: {
        ...((dateFilter.params as RelativeDateParams | undefined) ?? {}),
        delta: offset,
      },
    };
  }

  return normalized;
};

/**
 * Prepare a filter model to apply to the grid.
 * Remembers deltas and refreshes dateFrom from them (so "Today" stays today).
 */
export const relativeDatesForGrid = (
  filterModel: FilterModel | null | undefined,
): FilterModel | null => {
  if (filterModel == null) return null;

  const normalized = normalizeLegacyRelativeTypes(filterModel);
  rememberRelativeDates(normalized);

  const resolved = { ...normalized };
  for (const [column, filter] of Object.entries(resolved)) {
    if (!filter || typeof filter !== 'object') continue;

    const dateFilter = filter as Record<string, unknown>;
    if (dateFilter.filterType !== 'date') continue;

    resolved[column] = expandRollingDates(dateFilter);
  }

  return resolved;
};

/**
 * Prepare a filter model to save (cache / saved search).
 * Re-attaches remembered deltas that AG Grid stripped from the live model.
 * Drops remembered deltas that no longer match the live date values.
 */
export const relativeDatesForSave = (
  filterModel: FilterModel | null | undefined,
): FilterModel | null => {
  if (filterModel == null) return null;

  syncButtonClickIntoMap(filterModel);

  const saved = { ...filterModel };
  const activeColumns = new Set(Object.keys(saved));

  for (const column of [...deltasByColumn.keys()]) {
    if (!activeColumns.has(column)) {
      deltasByColumn.delete(column);
    }
  }

  for (const [column, filter] of Object.entries(saved)) {
    if (!filter || typeof filter !== 'object') continue;

    const remembered = deltasByColumn.get(column);
    if (!remembered) continue;

    const dateFilter = { ...(filter as Record<string, unknown>) };
    if (!liveFilterMatchesRelative(dateFilter, remembered)) {
      deltasByColumn.delete(column);
      continue;
    }

    saved[column] = {
      ...dateFilter,
      params: {
        ...((dateFilter.params as RelativeDateParams | undefined) ?? {}),
        ...remembered,
      },
    };
  }

  return saved;
};
